import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { ScaleLoader } from "react-spinners";
import classNames from "classnames";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { searchMedicineItems } from "../../services/medicineSearchService";
import { ItemBasicResponse } from "../../types/medicineSearch.types";
import styles from "./MedicineSearchBar.module.scss";

type BaseProps = {
  onSelect: (item: ItemBasicResponse) => void;
  placeholder?: string;
  className?: string;
};

/**
 * Single-select server-side medicine type-ahead against POST /search/item
 * (global item search, ItemBasicResponse). Each result row shows the item name
 * with an optional decorative thumbnail (the first IMAGE file).
 *
 * An accessible name is mandatory at the type level: pass EITHER `label`
 * (renders a visible <label htmlFor>) OR `ariaLabel` (sets aria-label). A call
 * site that provides neither is a compile error. Placeholder text alone is NOT
 * an accessible name.
 */
export type MedicineSearchBarProps = BaseProps &
  ({ label: string; ariaLabel?: never } | { ariaLabel: string; label?: never });

// Square thumbnail size (px). Fixed width/height on the <img> reserves the box
// before the image loads, so result rows never reflow (no layout shift).
const IMAGE_SIZE = 28;

// /search/item returns relative file paths and `thumbnail: null`, so the URL is
// built from the first IMAGE file's `location`, prefixed with the CDN base.
const CDN_BASE = process.env.REACT_APP_CDN_PATH ?? "";

const firstImageUrl = (item: ItemBasicResponse): string | undefined => {
  const image = item.files?.find((file) => file.type === "IMAGE");
  return image ? `${CDN_BASE}${image.location}` : undefined;
};

/**
 * Decorative product thumbnail for a result row. On load failure it is replaced
 * by a same-size placeholder so the row keeps its fixed height (no layout shift)
 * and the broken-image glyph never shows.
 */
const OptionImage = ({ src }: { src: string }) => {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span
        className={styles.optionImageFallback}
        data-testid="medicine-option-image-fallback"
        aria-hidden="true"
      />
    );
  }

  return (
    <img
      className={styles.optionImage}
      src={src}
      alt=""
      width={IMAGE_SIZE}
      height={IMAGE_SIZE}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const MedicineSearchBar = (props: MedicineSearchBarProps) => {
  const {
    onSelect,
    placeholder = "Pretražite artikle",
    className,
    label,
    ariaLabel,
  } = props as MedicineSearchBarProps & {
    label?: string;
    ariaLabel?: string;
  };

  const { query, onQueryChange, results, status, loading, reset } =
    useDebouncedSearch<ItemBasicResponse>(searchMedicineItems);

  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);

  const reactId = useId();
  const inputId = `medicine-search-${reactId}-input`;
  const listboxId = `medicine-search-${reactId}-listbox`;
  const optionId = (item: ItemBasicResponse) => `${listboxId}-opt-${item.id}`;

  // Reset the active-descendant cursor whenever the result set changes, so a
  // stale cursor can never index into a different (or shorter) list.
  useEffect(() => {
    setCursor(-1);
  }, [results]);

  // Containment-aware click-away. While the search is open — including DURING
  // loading — a mousedown anywhere outside this component closes it. Keeping it
  // active during loading is what stops a slow response from popping a dropdown
  // open after the user has already clicked elsewhere. (Selection on an option
  // happens on the option's own onMouseDown, which is inside the root, so it is
  // never treated as an outside click.)
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutsideMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideMouseDown);
    return () =>
      document.removeEventListener("mousedown", handleOutsideMouseDown);
  }, [open]);

  // Disjoint flags: a status row is NEVER part of the keyboard-nav list.
  const showResultsList = open && status === "results" && results.length > 0;
  const showStatusRow = open && (status === "empty" || status === "error");
  const showDropdown = showResultsList || showStatusRow;

  const selectItem = useCallback(
    (item: ItemBasicResponse) => {
      reset();
      setOpen(false);
      setCursor(-1);
      onSelect(item);
    },
    [reset, onSelect]
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onQueryChange(e.target.value);
    setOpen(true);
  };

  // Keyboard handling is SCOPED to the input — it never listens on `window`,
  // so Enter in another field can never fire a selection here.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter") {
      // ALWAYS swallow Enter so it can never implicitly submit a containing
      // <form>. Select only when a real, in-range result is highlighted — never
      // a status row, and never when the list is closed.
      e.preventDefault();
      if (showResultsList && cursor >= 0 && cursor < results.length) {
        selectItem(results[cursor]);
      }
      return;
    }
    if (!showResultsList) {
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => (c < results.length - 1 ? c + 1 : c));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => (c > 0 ? c - 1 : 0));
    }
  };

  const activeDescendant =
    showResultsList && cursor >= 0 && cursor < results.length
      ? optionId(results[cursor])
      : undefined;

  return (
    <div className={styles.MedicineSearchBar} ref={rootRef}>
      {label && (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      )}
      <div className={styles.inputWrapper}>
        <input
          id={inputId}
          className={classNames(styles.input, className)}
          type="text"
          name="medicine-search-box"
          autoComplete="off"
          maxLength={64}
          placeholder={placeholder}
          aria-label={ariaLabel}
          role="combobox"
          aria-expanded={showResultsList}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeDescendant}
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
        />
        {loading && (
          <span className={styles.spinner} aria-hidden="true">
            <ScaleLoader color="#00b2b1" height={14} width={3} />
          </span>
        )}

        {showDropdown && (
          <>
            {showStatusRow && (
              <div className={styles.statusRow} role="status">
                {status === "empty"
                  ? `Nema artikala koji odgovaraju pretrazi "${query.trim()}".`
                  : "Greška, pokušajte ponovo"}
              </div>
            )}
            {showResultsList && (
              <ul className={styles.listbox} id={listboxId} role="listbox">
                {results.map((item, i) => {
                  const imageUrl = firstImageUrl(item);
                  return (
                    <li
                      key={item.id}
                      id={optionId(item)}
                      className={classNames(styles.option, {
                        [styles.optionActive]: i === cursor,
                      })}
                      role="option"
                      aria-selected={i === cursor}
                      onMouseEnter={() => setCursor(i)}
                      onMouseDown={(e) => {
                        // Commit on pointer-down (preventDefault keeps focus) so
                        // selection lands before any focus change — never onBlur.
                        e.preventDefault();
                        selectItem(item);
                      }}
                    >
                      {imageUrl && <OptionImage src={imageUrl} />}
                      <span className={styles.optionName}>{item.name}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MedicineSearchBar;
